import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  DatasourceDownstreamTablesQuery,
  DatasourceDownstreamTablesQuery_publishedDatasources_downstreamTablesConnection_nodes,
  DatasourceDownstreamTablesQueryVariables,
} from '../../../gen-src/queries/DatasourceDownstreamTablesQuery';
import {
  DownstreamTablesFilteredByFieldQuery,
  DownstreamTablesFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/DownstreamTablesFilteredByFieldQuery';
import { TablesDisplayGridRowDataFragment } from '../../../gen-src/queries/TablesDisplayGridRowDataFragment';
import { DatabaseTableSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatasourceDownstreamTablesQueryGql } from '../../queries/DatasourceDownstreamTablesQueryGql';
import { DownstreamTablesFilteredByFieldQueryGql } from '../../queries/DownstreamTablesFilteredByFieldQueryGql';
import { TablesDisplayGrid } from '../sharedDisplayGrid/TablesDisplayGrid';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { getRowDataWithMaxCountsForTable } from '../table/functions/TableRowDataFunctions';
import { TablesDisplayGridRowData } from '../table/types/TablesDisplayGridRowData';

type QueryVariables = DatasourceDownstreamTablesQueryVariables;
type QueryResults = DatasourceDownstreamTablesQuery;
type QueryResultListNode = DatasourceDownstreamTablesQuery_publishedDatasources_downstreamTablesConnection_nodes;
type GridRowData = TablesDisplayGridRowData;

type QueryVariablesWithFilter = DownstreamTablesFilteredByFieldQueryVariables;
type QueryResultsWithFilter = DownstreamTablesFilteredByFieldQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].downstreamTablesConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && resultData.fieldsConnection.nodes[0].downstreamTablesConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  return resultData && resultData.fieldsConnection &&
    resultData.fieldsConnection.nodes && resultData.fieldsConnection.nodes[0];
}

function getRowDataForTable(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResult(resultData));
}

function getRowDataForTableForFilteredQuery(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type DatasourceDownstreamTablesQueryGridProps =
  GridSortProps<TablesValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredFieldId: string | null;
    readonly rowCount: number | null;
  };

export function DatasourceDownstreamTablesQueryGrid(props: DatasourceDownstreamTablesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyDownstreamTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredTablesLineageGridMessage();

  return props.filteredFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={DownstreamTablesFilteredByFieldQueryGql}
      partialQueryVariables={{
        selectedFieldId: props.filteredFieldId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatasourceDownstreamTablesQueryGql}
        partialQueryVariables={{
          vizportalUrlId: props.vizportalUrlId,
          orderBy: props.orderBy as DatabaseTableSortOrder,
          publishedConnectionsLineageEnabled
        }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForTable}
      >
        {({ rowStore }) =>
          <TablesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    );
}
